import React, { useState } from 'react';
import { Button, Chip, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import { ContentPad, PageShell, RecordFooter, RecordHeader, WorkArea } from 'components/record-shell/RecordShell';
import { moneyFormat } from 'helpers/moneyFormat';
import { usePermissionRules } from 'hooks/permissionRules';
import history from 'services/history';
import BudgetHistory from '../payments/BudgetHistory';
import BudgetPaymentsPanel from '../payments/BudgetPaymentsPanel';
import { useBudgetPayments } from '../payments/useBudgetPayments';
import CloseSurgerieDialog from './dialog/CloseSurgerieDialog';
import SurgerieGeneralTab from './form/SurgerieGeneralTab';
import SurgerieValuesTab from './form/SurgerieValuesTab';
import { useSurgerieRecord } from './hooks/useSurgerieRecord';
import SurgerieTab from './tab/SurgerieTab';

const SurgerieRecord: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { checkPermission } = usePermissionRules();
  const record = useSurgerieRecord(id);
  const payments = useBudgetPayments(Number(id), () => undefined);
  const [tab, setTab] = useState('main');
  const [closing, setClosing] = useState(false);

  const { budget, loading, saving, dirty } = record;
  const isOpen = budget?.status === 'open';
  const canEdit = isOpen && checkPermission('surgeries.open.edit');
  const canClose = isOpen && checkPermission('surgeries.open.close');
  const paid = payments.data?.value_paid ?? budget?.value_paid ?? 0;
  const listPath = budget?.status === 'closed' ? '/surgeries/closed' : '/surgeries/open';

  function goBack() {
    history.push(listPath);
  }

  function handleClose() {
    if (dirty) {
      toast.warning('Salve as alterações antes de fechar o orçamento');
      return;
    }

    setClosing(true);
  }

  // salvar e fechar geram registros novos no histórico
  function confirmClose() {
    setClosing(false);
    record.close()?.then(payments.reload);
  }

  return (
    <>
      <Appbar title="Orçamentos cirúrgicos" backAction={goBack} />

      {(saving || payments.saving) && <InsideSaving />}

      {budget && closing && (
        <CloseSurgerieDialog
          budget={{ id_int: budget.id_int, value_total: budget.value_total, value_paid: paid }}
          onConfirm={confirmClose}
          onExited={() => setClosing(false)}
        />
      )}

      <PageShell>
        <RecordHeader title={`Orçamento #${id}`} backAction={goBack}>
          {budget && (
            <>
              <Chip size="small" color={isOpen ? 'warning' : 'success'} label={isOpen ? 'Aberto' : 'Fechado'} />
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                label={`Venda ${moneyFormat(budget.value_total)}`}
              />
            </>
          )}
        </RecordHeader>

        <WorkArea>
          <SurgerieTab
            tab={tab}
            onChange={setTab}
            counts={{
              payments: (payments.data?.payments || []).filter(item => item.status === 'active').length,
              history: payments.data?.logs.length || 0,
            }}
          />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !budget ? (
              <NoData message="Não foi possível carregar o orçamento" />
            ) : tab === 'values' ? (
              <SurgerieValuesTab
                budget={budget}
                editable={canEdit}
                onChangeValue={record.changeValue}
                onChangePackage={record.changePackageValue}
              />
            ) : tab === 'payments' ? (
              <BudgetPaymentsPanel total={budget.value_total} paid={paid} payments={payments} />
            ) : tab === 'history' ? (
              <BudgetHistory logs={payments.data?.logs || []} />
            ) : (
              <SurgerieGeneralTab budget={budget} />
            )}
          </ContentPad>
        </WorkArea>

        {isOpen && !loading && (canEdit || canClose) && (
          <RecordFooter sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              {dirty ? 'Alterações não salvas' : ''}
            </Typography>
            <div style={{ display: 'flex', gap: 8 }}>
              {canClose && (
                <Button variant="outlined" size="small" disabled={saving} onClick={handleClose}>
                  Fechar orçamento
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={saving}
                  onClick={() => record.save()?.then(payments.reload)}
                >
                  Salvar alterações
                </Button>
              )}
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default SurgerieRecord;
